import type { Api } from '../core/api';
import { log } from '../core/log';

/**
 * VTeX adapter — listens to `vtex:*` DOM events and translates them to the
 * GA4-style e-commerce vocabulary the SDK speaks. Spec §5.
 *
 * The adapter is best-effort: VTeX's event shapes vary by storefront version,
 * so we sniff a few common payload paths instead of asserting a single one.
 */
export function detectVtex(): boolean {
	if (typeof window === 'undefined') return false;
	if ((window as any).vtex) return true;
	if (typeof location === 'undefined') return false;
	const host = location.host || '';
	return /\.vtexcommercestable\.com\.br/.test(host) || /\.myvtex\.com/.test(host) || /\.vtex\.com\.br/.test(host);
}

interface VtexProduct {
	sku?: string;
	productId?: string | number;
	name?: string;
	brand?: string;
	category?: string;
	categories?: string[];
	price?: number;
	quantity?: number;
	currency?: string;
	[k: string]: unknown;
}

/** VTeX often ships prices in cents (integer). Heuristic: >10000 + integer. */
function fromCents(value: unknown): number {
	const n = Number(value);
	if (!isFinite(n)) return 0;
	if (Number.isInteger(n) && Math.abs(n) >= 10_000) return n / 100;
	return n;
}

function mapItem(p: VtexProduct, index?: number): Record<string, unknown> {
	const item: Record<string, unknown> = {
		item_id: p.sku || String(p.productId || ''),
		item_name: p.name,
		item_brand: p.brand,
		item_category: p.category || (Array.isArray(p.categories) ? p.categories[0] : undefined),
		price: fromCents(p.price),
		quantity: p.quantity || 1,
	};
	if (index !== undefined) item.index = index;
	return item;
}

function unwrap<T = any>(detail: any): T {
	// VTeX sometimes nests payloads under e.detail, sometimes e directly.
	if (!detail) return {} as T;
	return (detail.detail || detail) as T;
}

export function installVtex(emblue: Api): void {
	if (!detectVtex()) return;
	log('vtex: adapter installed');

	window.addEventListener('vtex:productView', (e: any) => {
		const d = unwrap(e);
		const p = (d.product || d) as VtexProduct;
		emblue.track('view_item', {
			currency: p.currency || 'ARS',
			value: fromCents(p.price),
			items: [mapItem(p)],
		});
	});

	window.addEventListener('vtex:productClick', (e: any) => {
		const d = unwrap(e);
		const p = (d.product || d) as VtexProduct;
		emblue.track('select_item', {
			items: [mapItem(p)],
			item_list_id: d.list || d.listName,
			item_list_name: d.listName,
		});
	});

	window.addEventListener('vtex:productImpression', (e: any) => {
		const d = unwrap(e);
		const products = (d.products || d.impressions || (d.product ? [d.product] : [])) as VtexProduct[];
		if (!products.length) return;
		emblue.track('view_item_list', {
			item_list_id: d.list || d.listName,
			item_list_name: d.listName,
			items: products.map((p, i) => mapItem(p, i)),
		});
	});

	window.addEventListener('vtex:addToCart', (e: any) => {
		const d = unwrap(e);
		const items = (d.items || d.products || []) as VtexProduct[];
		emblue.track('add_to_cart', {
			currency: d.currency || 'ARS',
			value: items.reduce((acc, it) => acc + fromCents(it.price) * (it.quantity || 1), 0),
			items: items.map(mapItem),
		});
	});

	window.addEventListener('vtex:removeFromCart', (e: any) => {
		const d = unwrap(e);
		const items = (d.items || d.products || []) as VtexProduct[];
		emblue.track('remove_from_cart', {
			currency: d.currency || 'ARS',
			value: items.reduce((acc, it) => acc + fromCents(it.price) * (it.quantity || 1), 0),
			items: items.map(mapItem),
		});
	});

	window.addEventListener('vtex:cartLoaded', (e: any) => {
		const d = unwrap(e);
		const items = (d.items || d.products || []) as VtexProduct[];
		emblue.track('view_cart', {
			currency: d.currency || 'ARS',
			value: items.reduce((acc, it) => acc + fromCents(it.price) * (it.quantity || 1), 0),
			items: items.map(mapItem),
		});
	});

	window.addEventListener('vtex:orderPlaced', (e: any) => {
		const d = unwrap(e);
		const subtotal = fromCents(d.transactionTotal) - fromCents(d.transactionTax) - fromCents(d.transactionShipping);
		emblue.track('purchase', {
			transaction_id: d.transactionId,
			value: subtotal,
			currency: d.transactionCurrency || d.currency || 'ARS',
			tax: fromCents(d.transactionTax),
			shipping: fromCents(d.transactionShipping),
			coupon: d.coupon,
			items: ((d.transactionProducts || []) as VtexProduct[]).map(mapItem),
		});
	});

	window.addEventListener('vtex:userData', (e: any) => {
		const d = unwrap(e);
		const userId = d.userId || d.id;
		const email = d.email || d.emailAddress;
		// `vtex:userData` firing with an identity means the shopper is logged into
		// the store, so the opt-in it produces is labelled `vtex` rather than
		// collapsing into the generic identify/api sources.
		if (userId) {
			emblue.identify(String(userId), undefined, 'vtex');
		}
		// VTeX exposes the email in cleartext. We hash it client-side via
		// setEmail() so only the SHA-256 hex leaves the browser, and
		// subsequent batches carry email_sha256 in the envelope for
		// backend identity resolution.
		if (email && typeof email === 'string') {
			emblue.setEmail(email, 'vtex');
		}
	});

	window.addEventListener('vtex:internalSiteSearchView', (e: any) => {
		const d = unwrap(e);
		emblue.track('search', {
			search_term: d.term || d.searchTerm,
			results: d.results,
		});
	});
}
